import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link2Off } from "lucide-react"

import { api, ErrorApi } from "@/api/cliente"

/**
 * Los enlaces de sólo lectura que el bot ha mandado de esta tarea.
 *
 * ⚠ **Se cuentan, no se muestran.** El token no existe en ningún sitio después de
 * emitirse —en la base sólo queda su hash— así que no hay nada que enseñar aquí ni
 * que copiar. Lo único accionable es cortarlos.
 *
 * ⚠ **Se revocan todos de golpe.** Quien revoca lo hace porque algo se filtró y no
 * sabe cuál de los enlaces fue; dejar vivo el resto sería resolver a medias.
 */

type Props = {
  tareaId: number
  puedeRevocar: boolean
}

export function EnlacesTarea({ tareaId, puedeRevocar }: Props) {
  const clienteQuery = useQueryClient()

  const vivos = useQuery({
    queryKey: ["tarea", tareaId, "enlaces"],
    queryFn: () => api.get<{ vivos: number }>(`/api/v1/tareas/${tareaId}/enlaces`),
  })

  const revocar = useMutation({
    mutationFn: () => api.post(`/api/v1/tareas/${tareaId}/enlaces/revocar`),
    onSuccess: () => clienteQuery.invalidateQueries({ queryKey: ["tarea", tareaId, "enlaces"] }),
  })

  const cuantos = vivos.data?.vivos ?? 0
  if (cuantos === 0) return null

  return (
    <section className="mt-5">
      <h3 className="text-sm font-medium">Enlaces del bot</h3>
      <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
        <span data-testid="enlaces-vivos" className="tabular-nums">
          {cuantos}
        </span>{" "}
        {cuantos === 1 ? "enlace abre" : "enlaces abren"} esta tarea en sólo lectura. Caducan a los
        siete días y dejan de valer cuando la tarea se cierre.
      </p>
      {puedeRevocar && (
        <button
          type="button"
          data-testid="revocar-enlaces"
          disabled={revocar.isPending}
          onClick={() => revocar.mutate()}
          className="boton-secundario mt-2 flex items-center gap-2 px-2 py-1 text-xs disabled:opacity-50"
        >
          <Link2Off size={12} aria-hidden={true} />
          {revocar.isPending ? "Cortando…" : "Revocarlos"}
        </button>
      )}
      {revocar.isError && (
        <p role="alert" className="mt-1 text-xs" style={{ color: "var(--destructive)" }}>
          {revocar.error instanceof ErrorApi ? revocar.error.message : "No se pudieron revocar."}
        </p>
      )}
    </section>
  )
}
